import React, { useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './RetirementResultsCharts.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface RetirementResultsChartsProps {
  results: any;
  formatCurrency: (value: number) => string;
}

const RetirementResultsCharts: React.FC<RetirementResultsChartsProps> = ({
  results,
  formatCurrency
}) => {
  const [activeView, setActiveView] = useState<'balances' | 'income' | 'taxes'>('balances');

  if (!results || !results.representative_scenario || !results.representative_scenario.year_by_year) {
    return null;
  }

  const yearByYear = results.representative_scenario.year_by_year;

  // Prepare chart data
  const labels = yearByYear.map((y: any) => `Age ${y.age}`);
  const years = yearByYear.map((y: any) => y.year);

  // Balance progression data
  const balanceData = {
    labels,
    datasets: [
      {
        label: 'RRSP/RRIF',
        data: yearByYear.map((y: any) => y.balances.rrsp || 0),
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'TFSA',
        data: yearByYear.map((y: any) => y.balances.tfsa || 0),
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Non-Registered',
        data: yearByYear.map((y: any) => y.balances.non_registered || 0),
        borderColor: '#ffc107',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Income sources data (stacked)
  const incomeData = {
    labels,
    datasets: [
      {
        label: 'Employment',
        data: yearByYear.map((y: any) => y.employment_income || 0),
        backgroundColor: '#667eea',
        stack: 'income'
      },
      {
        label: 'CPP',
        data: yearByYear.map((y: any) => y.cpp_income || 0),
        backgroundColor: '#28a745',
        stack: 'income'
      },
      {
        label: 'OAS',
        data: yearByYear.map((y: any) => y.oas_final || 0),
        backgroundColor: '#20c997',
        stack: 'income'
      },
      {
        label: 'RRSP/RRIF',
        data: yearByYear.map((y: any) => y.rrsp_withdrawal || 0),
        backgroundColor: '#ffc107',
        stack: 'income'
      },
      {
        label: 'TFSA',
        data: yearByYear.map((y: any) => y.tfsa_withdrawal || 0),
        backgroundColor: '#17a2b8',
        stack: 'income'
      },
      {
        label: 'Investment Income',
        data: yearByYear.map((y: any) => y.investment_income || 0),
        backgroundColor: '#6c757d',
        stack: 'income'
      }
    ]
  };

  // Taxes and spending data
  const taxesData = {
    labels,
    datasets: [
      {
        label: 'Spending',
        data: yearByYear.map((y: any) => y.annual_spending || 0),
        backgroundColor: '#667eea',
        stack: 'outflows'
      },
      {
        label: 'Taxes',
        data: yearByYear.map((y: any) => y.taxes_paid || 0),
        backgroundColor: '#dc3545',
        stack: 'outflows'
      },
      {
        label: 'OAS Clawback',
        data: yearByYear.map((y: any) => y.oas_clawed_back || 0),
        backgroundColor: '#fd7e14',
        stack: 'outflows'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return '$' + (value / 1000) + 'k';
          }
        }
      }
    }
  };

  return (
    <div className="retirement-charts-container">
      <div className="charts-header">
        <h3>📊 Financial Projection</h3>
        <div className="view-toggle">
          <button
            className={activeView === 'balances' ? 'active' : ''}
            onClick={() => setActiveView('balances')}
          >
            Account Balances
          </button>
          <button
            className={activeView === 'income' ? 'active' : ''}
            onClick={() => setActiveView('income')}
          >
            Income Sources
          </button>
          <button
            className={activeView === 'taxes' ? 'active' : ''}
            onClick={() => setActiveView('taxes')}
          >
            Spending & Taxes
          </button>
        </div>
      </div>

      <div className="chart-wrapper">
        {activeView === 'balances' && (
          <div className="chart-container">
            <h4>Account Balances Over Time</h4>
            <div style={{height: '400px'}}>
              <Line data={balanceData} options={chartOptions} />
            </div>
            <p className="chart-note">
              💡 Balances show how your savings are drawn down and grow over time
            </p>
          </div>
        )}

        {activeView === 'income' && (
          <div className="chart-container">
            <h4>Annual Income by Source</h4>
            <div style={{height: '400px'}}>
              <Bar data={incomeData} options={{...chartOptions, scales: {...chartOptions.scales, x: {...chartOptions.scales.x, stacked: true}, y: {...chartOptions.scales.y, stacked: true}}}} />
            </div>
            <p className="chart-note">
              💡 See how income sources shift from employment → government benefits → withdrawals
            </p>
          </div>
        )}

        {activeView === 'taxes' && (
          <div className="chart-container">
            <h4>Annual Spending & Taxes</h4>
            <div style={{height: '400px'}}>
              <Bar data={taxesData} options={{...chartOptions, scales: {...chartOptions.scales, x: {...chartOptions.scales.x, stacked: true}, y: {...chartOptions.scales.y, stacked: true}}}} />
            </div>
            <p className="chart-note">
              💡 Tax-efficient withdrawal strategy minimizes taxes while maintaining lifestyle
            </p>
          </div>
        )}
      </div>

      {/* Quick Stats Below Chart */}
      <div className="chart-stats">
        <div className="stat">
          <span className="stat-label">Peak Balance</span>
          <span className="stat-value">
            {formatCurrency(Math.max(...yearByYear.map((y: any) =>
              (y.balances.rrsp || 0) + (y.balances.tfsa || 0) + (y.balances.non_registered || 0)
            )))}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Taxes Paid</span>
          <span className="stat-value">
            {formatCurrency(results.representative_scenario.total_lifetime_taxes)}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Total OAS Clawback</span>
          <span className="stat-value">
            {formatCurrency(results.representative_scenario.total_oas_clawback || 0)}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Years Tracked</span>
          <span className="stat-value">{yearByYear.length}</span>
        </div>
      </div>
    </div>
  );
};

export default RetirementResultsCharts;
